import React, { useState } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar, Typography, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, AppBar, Toolbar, IconButton, Drawer, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import HomeIcon from '@mui/icons-material/Home';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import FeedbackIcon from '@mui/icons-material/Feedback';
import InsightsIcon from '@mui/icons-material/Insights';
import MapIcon from '@mui/icons-material/Map';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryIcon from '@mui/icons-material/History';
import BusinessIcon from '@mui/icons-material/Business';
import { useOrg, useRole, useUser } from '../context/UserContext';
import { ROLE_LABELS } from '../utils/orgApi';
import { brand, colors, navItemSx, secondaryOnDarkButtonSx, topBarSx, TOP_BAR_HEIGHT_PX } from '../theme';
import { Wordmark } from './Wordmark';
import { useLayout } from '../context/LayoutContext';
import { BACKEND_URL } from '../utils/constants';

const SIDEBAR_WIDTH = 240; // px — desktop rail width / mobile drawer width
const MOBILE_TOP_BAR_HEIGHT_PX = 56;

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, getAccessTokenSilently } = useAuth0();
  const { user } = useUser();
  const org = useOrg();
  const { canEdit, isAdmin: isFirmAdmin, inFirm, role } = useRole();
  const { hideMainSidebar } = useLayout();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  // Get admin emails from environment variable
  const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',') || [];
  const isAdmin = adminEmails.includes(user?.email || '');

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  // Navigate and (on mobile) close the drawer.
  const handleNav = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const navItems = [
    { label: 'Your Models', icon: <HomeIcon />, path: '/' },
    // Viewers cannot create deals (enterprise batch 8; the server refuses too).
    ...(canEdit ? [{ label: 'Create Model', icon: <AddCircleOutlineIcon />, path: '/create-model' }] : []),
    { label: 'Dashboard', icon: <InsightsIcon />, path: '/dashboard' },
    { label: 'Deal Map', icon: <MapIcon />, path: '/map' },
  ];

  const bottomItems = [
    // Firm admins manage the team; an admin of an org of one sees it too,
    // since inviting the first colleague is how a firm starts.
    ...(isFirmAdmin ? [{ label: 'Team', icon: <GroupsIcon />, path: '/team' }] : []),
    { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    ...(isAdmin ? [
      { label: 'User Management', icon: <PeopleIcon />, path: '/admin/users' },
      { label: 'Firms', icon: <BusinessIcon />, path: '/admin/firms' },
      { label: 'Activity (all firms)', icon: <HistoryIcon />, path: '/admin/activity' },
      { label: 'Admin', icon: <AdminPanelSettingsIcon />, path: '/admin/model-types' },
    ] : []),
  ];

  const feedbackItem = { label: 'Submit Feedback', icon: <FeedbackIcon /> };

  // Sidebar inner content, shared between the desktop permanent rail and the
  // mobile temporary Drawer.
  const sidebarContent = (
    <>
      {/* Logo Section: same height as each page's top bar so the lines meet */}
      <Box
        sx={{
          height: TOP_BAR_HEIGHT_PX,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          borderBottom: `1px solid ${brand.lineDarkBg}`,
        }}
      >
        <Wordmark onNavigate={() => handleNav('/')} />
      </Box>

      {/* Main Navigation */}
      <List sx={{ flex: 1, px: 2, py: 2 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNav(item.path)}
                aria-current={isActive ? 'page' : undefined}
                sx={navItemSx(isActive)}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: brand.lineDarkBg }} />

      {/* Bottom Navigation */}
      <List sx={{ px: 2, py: 2 }}>
        {bottomItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNav(item.path)}
                aria-current={isActive ? 'page' : undefined}
                sx={navItemSx(isActive)}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => { setFeedbackOpen(true); setMobileOpen(false); }}
            sx={navItemSx(false)}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {feedbackItem.icon}
            </ListItemIcon>
            <ListItemText primary={feedbackItem.label} />
          </ListItemButton>
        </ListItem>
      </List>

      {/* User Profile Section */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${brand.lineDarkBg}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1,
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: brand.accent,
              color: brand.ink,
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {user?.email?.[0]?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: brand.textDarkBg,
                fontWeight: 600,
                fontSize: '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email?.split('@')[0] || 'User'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: brand.mutedDarkBg,
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email || ''}
            </Typography>
            {inFirm && org && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: brand.mutedDarkBg,
                  fontSize: '0.75rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {org.name}, {ROLE_LABELS[role]}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
          fullWidth
          size="small"
          sx={{ mt: 1, ...secondaryOnDarkButtonSx }}
        >
          Log out
        </Button>
      </Box>
    </>
  );

  // Whether to show any navigation chrome at all.
  const showNav = !hideMainSidebar;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile top bar with hamburger */}
      {showNav && isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            ...topBarSx,
            position: 'fixed',
            backgroundImage: 'none',
            zIndex: (t) => t.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ minHeight: MOBILE_TOP_BAR_HEIGHT_PX }}>
            <IconButton
              edge="start"
              aria-label="open navigation"
              onClick={() => setMobileOpen(true)}
              sx={{ color: colors.white, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Wordmark onNavigate={() => handleNav('/')} />
          </Toolbar>
        </AppBar>
      )}

      {/* Desktop permanent sidebar */}
      {showNav && !isMobile && (
        <Box
          component="nav"
          aria-label="Main"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            bgcolor: brand.ink,
            borderRight: `1px solid ${brand.lineDarkBg}`,
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            height: '100vh',
            zIndex: 1200,
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Mobile temporary drawer */}
      {showNav && isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
              bgcolor: brand.ink,
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Main Content Area: dense content sits on a light surface */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // minWidth:0 lets this flex child shrink below its content's intrinsic
          // width; without it, nowrap text forces the page wider than the viewport.
          minWidth: 0,
          marginLeft: showNav && !isMobile ? `${SIDEBAR_WIDTH}px` : 0,
          // Offset for the fixed mobile AppBar so content isn't hidden beneath it.
          mt: showNav && isMobile ? `${MOBILE_TOP_BAR_HEIGHT_PX}px` : 0,
          minHeight: '100vh',
          overflowX: 'hidden',
          bgcolor: brand.paper2,
          color: brand.textLightBg,
        }}
      >
        {children}
      </Box>

      {/* Feedback Dialog */}
      <Dialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please use this form to submit feedback, or explain any issue or question you may be having.
          </Typography>
          <TextField
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            placeholder="Describe your issue or question here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                const token = await getAccessTokenSilently();
                await fetch(`${BACKEND_URL}/api/issues`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  credentials: 'include',
                  body: JSON.stringify({
                    page: location.pathname,
                    subsection: '',
                    issue: feedbackMessage || ''
                  })
                });
              } catch (e) {
                console.error('Failed to submit issue', e);
              } finally {
                setFeedbackOpen(false);
                setFeedbackMessage('');
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
